import type { FormItemAriaIds } from '@faceup/ui'
import { InputPhone, Scale } from '@faceup/ui'
import { DatePicker, Input, InputNumber, MultiSelect, Select } from '@faceup/ui-base'
import {
  FORM_ITEMS,
  FormItemType,
  getTranslation,
  type Language,
  SHORT_STRING_MAX_LENGTH,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import moment from 'moment-timezone'
import { useRef } from 'react'
import { graphql } from '../../../../__generated__'
import type { Answer } from '../../../../hooks'
import { FileFormElement } from './FileFormElement'

const fragments = {
  FormElement_formItem: graphql(`
    fragment FormElement_formItem on FormItem {
      id
      formItemId
      type
      hasHint
      deletedAt
      isRequired(channelId: $channelIdHack)
      labelTranslations {
        language
        translation
      }
      hintTranslations {
        language
        translation
      }
      options(includeDeleted: $includeDeletedOptions) {
        id
        deletedAt
        order
        labelTranslations {
          translation
          language
        }
      }
      scaleStartLabelTranslations {
        language
        translation
      }
      scaleEndLabelTranslations {
        language
        translation
      }
    }
  `),
}

type FormElementProps = {
  item: ResultOf<typeof fragments.FormElement_formItem>
  language: Language
  defaultLanguage?: Language
  type: FormItemType
  values: string[] | File[]
  onChange: (answer: Answer) => void
  ariaIds?: FormItemAriaIds
}

export const FormElement = (props: FormElementProps) => {
  const { item, type, values, onChange, language, defaultLanguage, ariaIds } = props
  const singleValue = (values[0] as string) ?? ''
  const a11yProps = {
    ...(ariaIds?.hintId && { 'aria-describedby': ariaIds.hintId }),
    ...(ariaIds?.errorId && {
      'aria-errormessage': ariaIds.errorId,
      'aria-invalid': true as const,
    }),
  }
  /**
   * Only for Select: when the current option is deleted we still have to show it, but it's not possible to select it anymore.
   */
  const currentValue = useRef(singleValue)

  const handleElementChange = (values: string[] | File[], type: Answer['type'] = 'text') => {
    if (type === 'file') {
      onChange({
        values: values as File[],
        type,
      })
      return
    }
    onChange({
      values: values as string[],
      type,
    })
  }

  switch (type) {
    case FormItemType.SimpleText:
      return (
        <Input
          {...a11yProps}
          value={singleValue}
          onChange={e => {
            handleElementChange([e.target.value])
          }}
          maxLength={SHORT_STRING_MAX_LENGTH}
        />
      )
    case FormItemType.Select: {
      const options = item.options
      return (
        <Select
          {...a11yProps}
          style={{ width: '100%' }}
          value={singleValue}
          onChange={value => handleElementChange(value ? [value] : [])}
          allowClear
          options={options
            .filter(option => !option.deletedAt || option.id === currentValue.current)
            .map(option => ({
              label: getTranslation(option.labelTranslations, language, defaultLanguage),
              value: option.id,
              disabled: !!option.deletedAt,
            }))}
        />
      )
    }
    case FormItemType.MultiSelect: {
      const options = item.options
      return (
        <MultiSelect
          {...a11yProps}
          style={{ width: '100%' }}
          value={values as string[]}
          allowClear
          onChange={values => handleElementChange(values)}
          options={options
            .filter(option => !option.deletedAt)
            .map(option => ({
              label: getTranslation(option.labelTranslations, language, defaultLanguage),
              value: option.id,
              disabled: !!option.deletedAt,
            }))}
        />
      )
    }
    case FormItemType.MultilineText:
      return (
        <Input.TextArea
          {...a11yProps}
          value={singleValue}
          onChange={e => {
            handleElementChange([e.target.value])
          }}
          rows={6}
        />
      )
    case FormItemType.Date:
      return (
        <DatePicker
          {...a11yProps}
          value={singleValue ? moment(singleValue) : undefined}
          onChange={date => {
            handleElementChange([date?.toISOString() ?? ''])
          }}
          style={{
            width: '100%',
          }}
        />
      )
    case FormItemType.Email:
      return (
        <Input
          {...a11yProps}
          value={singleValue}
          onChange={e => {
            handleElementChange([e.target.value])
          }}
          maxLength={SHORT_STRING_MAX_LENGTH}
        />
      )
    case FormItemType.PhoneNumber:
      return (
        <InputPhone
          {...a11yProps}
          value={singleValue}
          language={language}
          onChange={value => {
            handleElementChange([value])
          }}
        />
      )
    case FormItemType.Scale: {
      const translatedOptions = item.options.map(option => {
        const label = getTranslation(option.labelTranslations, language, defaultLanguage)

        return {
          id: option.id,
          label,
          isActive: singleValue === option.id,
        }
      })

      return (
        <>
          <input style={{ width: 0, height: 0, visibility: 'hidden', position: 'absolute' }} />
          <Scale
            startLabel={
              item.scaleStartLabelTranslations
                ? getTranslation(item.scaleStartLabelTranslations, language, defaultLanguage)
                : undefined
            }
            endLabel={
              item.scaleEndLabelTranslations
                ? getTranslation(item.scaleEndLabelTranslations, language, defaultLanguage)
                : undefined
            }
            onChange={values => handleElementChange(values)}
            options={translatedOptions}
          />
        </>
      )
    }
    case FormItemType.File:
      return (
        <>
          {/* Invisible input prevents double trigger of upload dialog caused by `label` wrapped around */}
          <Input className='h-[0] w-[0] absolute invisible' />
          <FileFormElement
            key={item.id}
            name={item.id}
            multiple={true}
            maxCount={FORM_ITEMS[FormItemType.File].maxFormCount}
            className='w-full'
            onChange={({ fileList }) => {
              handleElementChange(
                fileList.map(file => file.originFileObj as File),
                'file'
              )
            }}
            anonymityInfo={{ enabled: true, language }}
          />
        </>
      )
    case FormItemType.Number:
      return (
        <InputNumber
          {...a11yProps}
          type='number'
          stringMode
          onChange={value => {
            const strVal = value === null ? '' : String(value)
            handleElementChange([strVal])
          }}
        />
      )
    case FormItemType.Matrix:
    case FormItemType.SenderName:
    case FormItemType.Classroom:
    case FormItemType.MoreInformation:
    case FormItemType.OrganizationalUnit:
    case FormItemType.Category:
      // it's rendered via moreInformationRender in CustomFormItems
      return null
    case FormItemType.Rating:
      // Form item Rating isn't supported in forms
      return null
  }
}
